import React, { useContext, useRef, memo, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import styles from "./HomeSecondSection.module.css";
import { CTAButton } from "./CTAButton";
import { HomeContext } from "../../utils/homeContext/HomeContext";

const HomeSecondSection = memo(() => {
  const { acfData } = useContext(HomeContext);
  const containerRef = useRef(null); // ✅ Create ref

  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!acfData) return null;

  return (
    <motion.div
      ref={containerRef} // ✅ Attach ref to the div
      className={styles.HomeSecondSectionContainer}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: "easeOut" }}
      viewport={{ once: true, amount: 0.5 }}
    >
      {isHydrated && <AnimatedContent containerRef={containerRef} acfData={acfData} />}
    </motion.div>
  );
});

const AnimatedContent = ({ containerRef, acfData }) => {
  // ✅ Call `useScroll` inside the component where `ref` is attached
  const { scrollYProgress } = useScroll({
    target: containerRef,
    layoutEffect: false, // ✅ Fix hydration issue
    offset: ["start 80%", "start 20%"],
  });

  // 🎬 Scroll Animations
  const textOpacity = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const textY = useTransform(scrollYProgress, [0, 1], [50, 0]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [0.85, 1]);
  const imageY = useTransform(scrollYProgress, [0, 1], ["30%", "0%"]);
  const maskClip = useTransform(scrollYProgress, [0, 1], [
    "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)",
    "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
  ]);

  return (
    <>
      {/* ✅ Text Section */}
      <motion.div className={styles.textColumn} style={{ opacity: textOpacity, y: textY }}>
        <div className={styles.textContent}>
          <h1 className={styles.heading}>
            <span>WE</span>
            <br />
            <span className={styles.headingHighlight}>MADE</span>
            <br />
            <span>VISUAL STORIES</span>
          </h1>
          <h6>
            At our creative studio, we capture the essence of brands through
            design, film, and storytelling. Every frame, every pixel, every
            detail is crafted to make an impact.
          </h6>
          <CTAButton />
        </div>
      </motion.div>

      {/* ✅ Image Section */}
      <motion.div className={styles.imageColumn} style={{ y: imageY, scale: imageScale }}>
        <motion.div className={styles.imageOverlay} animate={{ clipPath: maskClip }} transition={{ duration: 1.5, ease: "easeInOut" }}>
          <motion.div className={styles.circleTextContainer} animate={{ opacity: 1 }} transition={{ duration: 1.5, ease: "easeInOut" }}>
            <svg className={styles.rotateSvg} viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" animate={{ rotate: [0, 360] }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }}>
              <defs>
                <path id="circlePath" d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0" />
              </defs>
              <text>
                <textPath href="#circlePath" textLength="471">
                  CREATIVITY UNLEASHED - {acfData.mainBrandContent.toUpperCase()} -
                </textPath>
              </text>
            </svg>
          </motion.div>
        </motion.div>

        {/* 🖼 Image */}
        <img
          loading="lazy"
          src="https://studiomaamangala.com/wp-content/uploads/2025/12/05-scaled.webp"
          className={styles.HomeSecondSectionImage}
          alt="Digital creation visualization"
          style={{ scale: imageScale, y: imageY }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
        />
      </motion.div>
    </>
  );
};

export default HomeSecondSection;
